import { useMemo } from "react";
import Button from "app/storybookComponents/Button";
import SimpleModal from "app/components/Modals/SimpleModal";
import { getPeopleOrPerson } from "utils/helperFunctions";
import {
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInCustomList,
  updateWeeklyCheckInCompanySettings,
  selectUpdateWeeklyCheckInCompanySettingsStatus,
  clearWeeklyCheckInCustomList,
} from "../slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { getTotalPeopleInSelectedEntitiesFromCustomList } from "../helpers";
import { selectAllUserSubordinates } from "app/containers/UserSettings/slice";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../constants";

interface DisableWeeklyCheckInConfirmationModalProps {
  show: boolean;
  onHide: () => void;
}

const DisableWeeklyCheckInConfirmationModal = ({
  show,
  onHide,
}: Readonly<DisableWeeklyCheckInConfirmationModalProps>) => {
  const dispatch = useAppDispatch();
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const companyWeeklyCheckInCustomList = useAppSelector(
    selectWeeklyCheckInCustomList
  );
  const users = useAppSelector(selectAllCompanyUsersById);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const teams = useAppSelector(selectTeamsByTeamId);
  const departments = useAppSelector(selectDepartments);
  const updateWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectUpdateWeeklyCheckInCompanySettingsStatus
  );

  const peopleCount = useMemo(() => {
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      return Object.keys(users).length;
    }

    return getTotalPeopleInSelectedEntitiesFromCustomList(
      companyWeeklyCheckInCustomList,
      allUsersSubordinates,
      teams,
      departments
    );
  }, [
    weeklyCheckInCompanySettings,
    companyWeeklyCheckInCustomList,
    allUsersSubordinates,
    teams,
    departments,
    users,
  ]);

  const handleConfirm = async () => {
    await dispatch(
      updateWeeklyCheckInCompanySettings({
        hasWeeklyCheckInsEnabledForEntireCompany: false,
        hasWeeklyCheckInsEnabled: false,
      })
    );
    dispatch(clearWeeklyCheckInCustomList());
    onHide();
  };

  const areButtonsDisabled =
    updateWeeklyCheckInCompanySettingsStatus === "loading";

  return (
    <SimpleModal
      show={show}
      onHide={onHide}
      title={`Disable ${WEEKLY_CHECK_IN_FEATURE_NAME}s`}
    >
      <hr className="m-0" />
      <div className="text-center">
        <h3>
          Are you sure you want to disable {WEEKLY_CHECK_IN_FEATURE_NAME}s?
        </h3>
        <p style={{ marginTop: "8px", padding: "0 20px" }}>
          You are disabling {WEEKLY_CHECK_IN_FEATURE_NAME.toLowerCase()}s for{" "}
          {peopleCount} {getPeopleOrPerson(peopleCount)} in your organization.
          They will no longer receive emails to complete the check-in or email
          reports generated each cycle.
        </p>
      </div>
      <div className="row-gap-10px justify-content-center mb-2">
        <Button
          onClick={onHide}
          variant="secondary-blue"
          disabled={areButtonsDisabled}
        >
          Back
        </Button>
        <Button onClick={handleConfirm} disabled={areButtonsDisabled}>
          Confirm
        </Button>
      </div>
    </SimpleModal>
  );
};

export default DisableWeeklyCheckInConfirmationModal;
